/**
 * Dyna Agency — Bilingual Email Template System (EN / HE)
 * 30 templates across 4 categories.
 * Each function: (data: Record<string, string>, lang: Lang) => { subject, html, text }
 * All content runs through replaceVariables() from templateEngine.
 *
 * Sign-off (no phone number, per agency policy):
 *   EN: Pinch & Ezra · Dyna Agency · https://www.dyna-agency.com · hello@dyna-agency.com
 *   HE: פינץ׳ ועזרא · סוכנות DYNA · https://www.dyna-agency.com · hello@dyna-agency.com
 */

import { replaceVariables } from './templateEngine';

// ── Types ──────────────────────────────────────────────────────────────────────

export type Lang = 'en' | 'he';
export type TemplateResult = { subject: string; html: string; text: string };

// ── Shared CSS (inline for email client compatibility) ─────────────────────────

// Brand-styled CSS for transactional emails. Inline-only — email clients
// strip <style> in many cases, so the wrapper also applies inline styles.
const CSS = `
body,table,td,a{-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%}
body{margin:0;padding:0;background:#0E0B14;font-family:Georgia,'Times New Roman',serif;color:#FDF8F0}
.bg{background:#0E0B14;background-image:radial-gradient(ellipse at top,#1A1228 0%,#0E0B14 60%);padding:48px 16px}
.w{max-width:600px;margin:0 auto}
.shell{background:#15101E;border:1px solid rgba(156,139,194,.18);border-radius:18px;padding:44px 40px;box-shadow:0 8px 32px rgba(0,0,0,.4)}
.hd{text-align:center;padding-bottom:28px;border-bottom:1px solid rgba(156,139,194,.18);margin-bottom:32px}
.logo{font-size:26px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#FDF8F0;font-family:Georgia,'Times New Roman',serif}
.logo span{color:#AA8BDA}
.tagline{font-size:10px;letter-spacing:.32em;text-transform:uppercase;color:#9C8BC2;font-family:'Courier New',monospace;margin-top:8px}
h1{font-family:Georgia,'Times New Roman',serif;font-size:28px;font-weight:700;color:#FDF8F0;margin:0 0 20px;line-height:1.22;letter-spacing:.01em}
h2{font-family:Georgia,'Times New Roman',serif;font-size:20px;font-weight:700;color:#FDF8F0;margin:24px 0 12px;line-height:1.3}
p{font-size:15px;line-height:1.75;color:#CABBDC;margin:0 0 14px}
strong{color:#FDF8F0;font-weight:600}
em{color:#AA8BDA;font-style:italic}
a{color:#AA8BDA}
a.btn{display:inline-block;background:linear-gradient(135deg,#5A4666 0%,#7B5A99 100%);color:#FDF8F0!important;text-decoration:none;padding:14px 34px;border-radius:999px;font-size:12px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;margin:12px 0 22px;font-family:Georgia,'Times New Roman',serif}
.card{background:#1C1528;border:1px solid rgba(156,139,194,.22);border-radius:14px;padding:22px 26px;margin:22px 0}
.lbl{font-size:10px;letter-spacing:.24em;text-transform:uppercase;color:#9C8BC2;font-family:'Courier New',monospace;margin-bottom:4px;margin-top:14px}
.lbl:first-child{margin-top:0}
.val{font-size:15px;color:#FDF8F0;font-weight:500;margin-bottom:0;line-height:1.4}
.signoff{margin-top:32px;padding-top:24px;border-top:1px solid rgba(156,139,194,.15);font-size:14px;color:#CABBDC;line-height:1.7}
.ft{margin-top:32px;padding-top:24px;border-top:1px solid rgba(156,139,194,.15);text-align:center}
.ft p{font-size:12px;color:#6E5E9A;margin:3px 0;line-height:1.5;font-family:Georgia,'Times New Roman',serif}
.ft a{color:#9C8BC2;text-decoration:none}
.ft .meta{font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#574286;font-family:'Courier New',monospace;margin-top:10px}
`;

// ── Brand-safe inline font stacks (email clients strip <style>) ────────────────

// Display font fallback for headings/CTAs (Coolvetica → Arial Black on email).
const FONT_DISPLAY = `'Arial Black','Arial Bold',Gadget,Arial,sans-serif`;
// Body font fallback (Geist → system stack on email).
const FONT_BODY    = `-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif`;
// Mono fallback for labels/eyebrows (JetBrains Mono → Courier New on email).
const FONT_MONO    = `'Courier New',Courier,monospace`;

const INLINE_H1     = `font-family:${FONT_DISPLAY};font-weight:900;font-size:28px;color:#FDF8F0;margin:0 0 20px;line-height:1.22;letter-spacing:.02em;text-transform:uppercase`;
const INLINE_H2     = `font-family:${FONT_DISPLAY};font-weight:900;font-size:20px;color:#FDF8F0;margin:24px 0 12px;line-height:1.3;letter-spacing:.02em;text-transform:uppercase`;
const INLINE_H3     = `font-family:${FONT_DISPLAY};font-weight:900;font-size:16px;color:#FDF8F0;margin:18px 0 10px;line-height:1.35;letter-spacing:.02em;text-transform:uppercase`;
const INLINE_P      = `font-family:${FONT_BODY};font-weight:300;font-size:15px;line-height:1.75;color:#CABBDC;margin:0 0 14px`;
const INLINE_LBL    = `font-family:${FONT_MONO};font-size:11px;letter-spacing:.24em;text-transform:uppercase;color:#9C8BC2;margin-bottom:4px;margin-top:14px`;
const INLINE_VAL    = `font-family:${FONT_BODY};font-weight:500;font-size:15px;color:#FDF8F0;margin-bottom:0;line-height:1.4`;
const INLINE_BTN    = `display:inline-block;background:linear-gradient(135deg,#5A4666 0%,#7B5A99 100%);color:#FDF8F0;text-decoration:none;padding:14px 34px;border-radius:999px;font-size:12px;font-weight:900;letter-spacing:.14em;text-transform:uppercase;margin:12px 0 22px;font-family:${FONT_DISPLAY}`;

// Walks the body HTML and adds inline `style` attributes to every element that
// would otherwise rely on the (stripped) <style> block. Idempotent — if an
// element already has a style attribute, the inline font fallback is prepended
// so the existing inline rules win on conflicts.
function inlineEmailStyles(html: string): string {
  const inject = (open: string, baseStyle: string): string => {
    if (/style\s*=/.test(open)) {
      return open.replace(/style\s*=\s*"([^"]*)"/, (_m, existing) => `style="${baseStyle};${existing}"`);
    }
    return open.replace(/^<(\w+)/, `<$1 style="${baseStyle}"`);
  };
  return html
    .replace(/<h1\b[^>]*>/gi, (m) => inject(m, INLINE_H1))
    .replace(/<h2\b[^>]*>/gi, (m) => inject(m, INLINE_H2))
    .replace(/<h3\b[^>]*>/gi, (m) => inject(m, INLINE_H3))
    .replace(/<p\b[^>]*>/gi,  (m) => inject(m, INLINE_P))
    .replace(/<div\b[^>]*class="[^"]*\blbl\b[^"]*"[^>]*>/gi, (m) => inject(m, INLINE_LBL))
    .replace(/<div\b[^>]*class="[^"]*\bval\b[^"]*"[^>]*>/gi, (m) => inject(m, INLINE_VAL))
    .replace(/<a\b[^>]*class="[^"]*\bbtn\b[^"]*"[^>]*>/gi,   (m) => inject(m, INLINE_BTN));
}

// ── Brand helpers (all inline-styled for email-client safety) ──────────────────

function header(lang: Lang): string {
  const tagline = lang === 'he' ? 'עיצוב · בנייה · צמיחה' : 'Design · Build · Grow';
  return `<div class="hd" style="text-align:center;padding-bottom:28px;border-bottom:1px solid rgba(156,139,194,.18);margin-bottom:32px">
    <div class="logo" style="font-family:${FONT_DISPLAY};font-size:26px;font-weight:900;letter-spacing:.12em;text-transform:uppercase;color:#FDF8F0">DYNA<span style="color:#AA8BDA">.</span></div>
    <div class="tagline" style="font-family:${FONT_MONO};font-size:10px;letter-spacing:.32em;text-transform:uppercase;color:#9C8BC2;margin-top:8px">${tagline}</div>
  </div>`;
}

function signoff(lang: Lang): string {
  const wrap = `margin-top:32px;padding-top:24px;border-top:1px solid rgba(156,139,194,.15)`;
  if (lang === 'he') {
    return `<div class="signoff" style="${wrap}">
      <p style="${INLINE_P};margin:0 0 4px">בברכה,</p>
      <p style="${INLINE_P};margin:0;color:#FDF8F0;font-weight:600">פינץ׳ ועזרא</p>
      <p style="${INLINE_P};margin:0;font-size:13px;color:#9C8BC2">סוכנות DYNA</p>
    </div>`;
  }
  return `<div class="signoff" style="${wrap}">
    <p style="${INLINE_P};margin:0 0 4px">Best,</p>
    <p style="${INLINE_P};margin:0;color:#FDF8F0;font-weight:600">Pinch &amp; Ezra</p>
    <p style="${INLINE_P};margin:0;font-size:13px;color:#9C8BC2">Dyna Agency</p>
  </div>`;
}

function footer(lang: Lang): string {
  const ft   = `margin-top:32px;padding-top:24px;border-top:1px solid rgba(156,139,194,.15);text-align:center`;
  const ftP  = `font-family:${FONT_BODY};font-weight:300;font-size:12px;color:#6E5E9A;margin:3px 0;line-height:1.5`;
  const meta = `font-family:${FONT_MONO};font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#574286;margin-top:10px`;
  if (lang === 'he') {
    return `<div class="ft" style="${ft}">
      <p style="${ftP}"><a href="https://www.dyna-agency.com" style="color:#9C8BC2;text-decoration:none">www.dyna-agency.com</a> · <a href="mailto:hello@dyna-agency.com" style="color:#9C8BC2;text-decoration:none">hello@dyna-agency.com</a></p>
      <p class="meta" style="${meta}">© ${new Date().getFullYear()} DYNA · ירושלים, ישראל</p>
    </div>`;
  }
  return `<div class="ft" style="${ft}">
    <p style="${ftP}"><a href="https://www.dyna-agency.com" style="color:#9C8BC2;text-decoration:none">www.dyna-agency.com</a> · <a href="mailto:hello@dyna-agency.com" style="color:#9C8BC2;text-decoration:none">hello@dyna-agency.com</a></p>
    <p class="meta" style="${meta}">© ${new Date().getFullYear()} DYNA · Jerusalem, IL</p>
  </div>`;
}

/**
 * Wraps body HTML with branded header, directional layout, and sign-off footer.
 * Exported so edge functions can wrap custom content using the same shell.
 * Email clients strip <style>, so every element receives inline font fallbacks.
 */
export function wrapEmail(content: string, lang: Lang): string {
  const dir   = lang === 'he' ? 'rtl' : 'ltr';
  const align = lang === 'he' ? 'right' : 'left';
  const inlinedContent = inlineEmailStyles(content);
  const bodyStyle  = `margin:0;padding:0;background:#0E0B14;font-family:${FONT_BODY};color:#FDF8F0`;
  const bgStyle    = `background:#0E0B14;background-image:radial-gradient(ellipse at top,#1A1228 0%,#0E0B14 60%);padding:48px 16px`;
  const shellStyle = `background:#15101E;border:1px solid rgba(156,139,194,.18);border-radius:18px;padding:44px 40px;box-shadow:0 8px 32px rgba(0,0,0,.4);text-align:${align}`;
  return `<!DOCTYPE html>
<html dir="${dir}" lang="${lang}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="dark">
<meta name="supported-color-schemes" content="dark">
<title>Dyna Agency</title>
<style>${CSS}</style>
</head>
<body style="${bodyStyle}">
<div class="bg" style="${bgStyle}">
  <table role="presentation" class="w" cellpadding="0" cellspacing="0" border="0" align="center" style="border-collapse:collapse;max-width:600px;margin:0 auto">
    <tr><td>
      <div class="shell" style="${shellStyle}">
${header(lang)}
${inlinedContent}
${signoff(lang)}
      </div>
${footer(lang)}
    </td></tr>
  </table>
</div>
</body>
</html>`;
}

// ── Plain text extractor ───────────────────────────────────────────────────────

function toText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ── Core build helper ──────────────────────────────────────────────────────────

function build(
  data: Record<string, string>,
  lang: Lang,
  en: { subject: string; body: string },
  he: { subject: string; body: string }
): TemplateResult {
  const tpl     = lang === 'he' ? he : en;
  const subject = replaceVariables(tpl.subject, data);
  const body    = replaceVariables(tpl.body, data);
  const html    = wrapEmail(body, lang);
  const text    = toText(html);
  return { subject, html, text };
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 1 — LEAD & SALES (11 templates)
// ═══════════════════════════════════════════════════════════════════════════════

/** 1. Sent immediately after lead submits the booking form. */
export function inquiryResponse(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Re: Your Inquiry to Dyna Agency',
    body: `
<h1>We got your message.</h1>
<p>Hi {{first_name}},</p>
<p>Thanks for reaching out to Dyna Agency. We've received your message and one of us will be in touch within 24 hours.</p>
<p>In the meantime, feel free to <a href="https://www.dyna-agency.com/work">browse our work</a> or <a href="https://www.dyna-agency.com/packages">explore our packages</a>.</p>
<p>Talk soon.</p>`,
  }, {
    subject: 'בקשתך התקבלה — סוכנות DYNA',
    body: `
<h1>קיבלנו את ההודעה שלך.</h1>
<p>שלום {{first_name}},</p>
<p>תודה שפנית לסוכנות DYNA. קיבלנו את פנייתך ואחד מאיתנו יחזור אליך תוך 24 שעות.</p>
<p>בינתיים, מוזמן/ת <a href="https://www.dyna-agency.com/work">לעיין בפרויקטים שלנו</a> או <a href="https://www.dyna-agency.com/packages">לבדוק את החבילות שלנו</a>.</p>
<p>נדבר בקרוב.</p>`,
  });
}

/** 2. Sent when a discovery call is booked. */
export function discoveryCallConfirmation(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Discovery Call is Confirmed ✅',
    body: `
<h1>Discovery call confirmed.</h1>
<p>Hi {{first_name}},</p>
<p>Your 30-minute discovery call with Dyna Agency is confirmed.</p>
<div class="card">
  <div class="lbl">Date</div><div class="val">{{booking_date}}</div>
  <div class="lbl">Time</div><div class="val">{{booking_time}}</div>
  <div class="lbl">Duration</div><div class="val">30 minutes</div>
</div>
<p>We'll cover your goals, timeline, and how we can help. Come with any questions.</p>
<a href="{{booking_link}}" class="btn">Add to Calendar</a>`,
  }, {
    subject: 'השיחה שלך אושרה ✅',
    body: `
<h1>שיחת גילוי אושרה.</h1>
<p>שלום {{first_name}},</p>
<p>שיחת הגילוי שלך עם סוכנות DYNA בת 30 דקות אושרה.</p>
<div class="card">
  <div class="lbl">תאריך</div><div class="val">{{booking_date}}</div>
  <div class="lbl">שעה</div><div class="val">{{booking_time}}</div>
  <div class="lbl">משך</div><div class="val">30 דקות</div>
</div>
<p>נדבר על המטרות שלך, לוח הזמנים ואיך נוכל לעזור. בואו עם שאלות.</p>
<a href="{{booking_link}}" class="btn">הוסף ליומן</a>`,
  });
}

/** 3. Reminder sent 24 hours before a discovery call. */
export function discoveryCallReminder24(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Reminder: Your Call is Tomorrow',
    body: `
<h1>See you tomorrow.</h1>
<p>Hi {{first_name}},</p>
<p>Just a reminder — your discovery call with Dyna Agency is <strong>tomorrow at {{booking_time}}</strong>.</p>
<div class="card">
  <div class="lbl">Date</div><div class="val">{{booking_date}}</div>
  <div class="lbl">Time</div><div class="val">{{booking_time}}</div>
</div>
<a href="{{booking_link}}" class="btn">View Details</a>`,
  }, {
    subject: 'תזכורת: השיחה מחר',
    body: `
<h1>נתראה מחר.</h1>
<p>שלום {{first_name}},</p>
<p>תזכורת — שיחת הגילוי שלך עם סוכנות DYNA היא <strong>מחר בשעה {{booking_time}}</strong>.</p>
<div class="card">
  <div class="lbl">תאריך</div><div class="val">{{booking_date}}</div>
  <div class="lbl">שעה</div><div class="val">{{booking_time}}</div>
</div>
<a href="{{booking_link}}" class="btn">הצג פרטים</a>`,
  });
}

/** 4. Reminder sent 1 hour before a discovery call. */
export function discoveryCallReminder1hr(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Call Starts in 1 Hour',
    body: `
<h1>Call starts in 1 hour.</h1>
<p>Hi {{first_name}},</p>
<p>Your discovery call with Dyna Agency starts in about an hour at <strong>{{booking_time}}</strong>.</p>
<a href="{{booking_link}}" class="btn">Join Now</a>`,
  }, {
    subject: 'השיחה מתחילה בעוד שעה',
    body: `
<h1>השיחה מתחילה בעוד שעה.</h1>
<p>שלום {{first_name}},</p>
<p>שיחת הגילוי שלך עם סוכנות DYNA מתחילה בעוד כשעה, בשעה <strong>{{booking_time}}</strong>.</p>
<a href="{{booking_link}}" class="btn">הצטרף עכשיו</a>`,
  });
}

/** 5. Sent when a custom proposal is delivered. Links to /proposal/:token. */
export function proposalSent(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Dyna Agency Proposal is Ready',
    body: `
<h1>Your proposal is ready.</h1>
<p>Hi {{first_name}},</p>
<p>We've put together a custom proposal for <strong>{{business_name}}</strong>. It covers scope, timeline, and investment — tailored to what you told us.</p>
<p>Take your time. If you have questions or want to adjust anything, just reply here.</p>
<a href="{{proposal_link}}" class="btn">View Your Proposal &rarr;</a>
<p style="font-size:13px;color:#9C8BC2;margin-top:8px"><em>This proposal is valid for 14 days.</em></p>`,
  }, {
    subject: 'ההצעה שלך מוכנה לצפייה',
    body: `
<h1>ההצעה שלך מוכנה.</h1>
<p>שלום {{first_name}},</p>
<p>הכנו הצעה מותאמת אישית עבור <strong>{{business_name}}</strong>. היא כוללת היקף, לוח זמנים והשקעה — בהתאם למה שסיפרת לנו.</p>
<p>קרא/י בנחת. שאלות או שינויים? פשוט השב/י כאן.</p>
<a href="{{proposal_link}}" class="btn">צפה בהצעה &larr;</a>
<p style="font-size:13px;color:#9C8BC2;margin-top:8px"><em>ההצעה בתוקף ל-14 ימים.</em></p>`,
  });
}

/** 5b. Internal notification — client accepted their proposal. */
export function proposalAccepted(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: '{{business_name}} Accepted Their Proposal',
    body: `
<h1>Proposal accepted.</h1>
<p><strong>{{client_name}}</strong> from <strong>{{business_name}}</strong> just accepted their proposal.</p>
<div class="card">
  <div class="lbl">Type</div><div class="val">{{proposal_type}}</div>
  <div class="lbl">Package</div><div class="val">{{package}}</div>
  <div class="lbl">Price</div><div class="val">{{price_display}}</div>
</div>
<p>A draft contract has been generated. Review it in admin and send it to the client when ready.</p>
<a href="{{admin_link}}" class="btn">Review Contract &rarr;</a>`,
  }, {
    subject: '{{business_name}} קיבלו את ההצעה',
    body: `
<h1>ההצעה התקבלה.</h1>
<p><strong>{{client_name}}</strong> מ-<strong>{{business_name}}</strong> קיבל/ה את ההצעה.</p>
<div class="card">
  <div class="lbl">סוג</div><div class="val">{{proposal_type}}</div>
  <div class="lbl">חבילה</div><div class="val">{{package}}</div>
  <div class="lbl">מחיר</div><div class="val">{{price_display}}</div>
</div>
<p>טיוטת חוזה נוצרה. עבור לאדמין לבדיקה ושליחה ללקוח.</p>
<a href="{{admin_link}}" class="btn">בדוק חוזה &larr;</a>`,
  });
}

/** 5c. Internal notification — client declined their proposal. */
export function proposalDeclined(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: '{{business_name}} Declined Their Proposal',
    body: `
<h1>Proposal declined.</h1>
<p><strong>{{client_name}}</strong> from <strong>{{business_name}}</strong> declined their proposal.</p>
<div class="card">
  <div class="lbl">Reason</div><div class="val">{{decline_reason}}</div>
  <div class="lbl">Their email</div><div class="val">{{client_email}}</div>
</div>
<p>Worth a follow-up?</p>`,
  }, {
    subject: '{{business_name}} דחו את ההצעה',
    body: `
<h1>ההצעה נדחתה.</h1>
<p><strong>{{client_name}}</strong> מ-<strong>{{business_name}}</strong> דחה/תה את ההצעה.</p>
<div class="card">
  <div class="lbl">סיבה</div><div class="val">{{decline_reason}}</div>
  <div class="lbl">אימייל</div><div class="val">{{client_email}}</div>
</div>
<p>שווה מעקב?</p>`,
  });
}

/** 6. Follow-up 3 days after proposal was sent with no response. */
export function proposalFollowUp3(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Following Up on Your Proposal',
    body: `
<h1>Checking in.</h1>
<p>Hi {{first_name}},</p>
<p>We sent over a proposal for <strong>{{business_name}}</strong> a few days ago and wanted to see if you had any questions.</p>
<p>We're flexible on scope and timing — happy to jump on a quick call if that helps.</p>
<a href="{{booking_link}}" class="btn">Book a Quick Call</a>`,
  }, {
    subject: 'מעקב על ההצעה שלך',
    body: `
<h1>רק לבדוק.</h1>
<p>שלום {{first_name}},</p>
<p>שלחנו הצעה עבור <strong>{{business_name}}</strong> לפני כמה ימים ורצינו לדעת אם יש שאלות.</p>
<p>אנחנו גמישים בהיקף ובתזמון — שמחים לשיחה קצרה אם זה עוזר.</p>
<a href="{{booking_link}}" class="btn">קבע שיחה</a>`,
  });
}

/** 7. Final follow-up 7 days after proposal with no response. */
export function proposalFollowUp7(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Last Check-In on Your Proposal',
    body: `
<h1>Last check-in.</h1>
<p>Hi {{first_name}},</p>
<p>One last follow-up on the proposal for <strong>{{business_name}}</strong>. We don't want to be pushy — just want to make sure you have what you need to decide.</p>
<p>If the timing isn't right, no worries at all. We'd love to work with you whenever it is.</p>
<a href="{{booking_link}}" class="btn">Let's Talk</a>`,
  }, {
    subject: 'בדיקה אחרונה על ההצעה',
    body: `
<h1>בדיקה אחרונה.</h1>
<p>שלום {{first_name}},</p>
<p>מעקב אחרון על ההצעה עבור <strong>{{business_name}}</strong>. לא רוצים ללחוץ — רק רוצים לוודא שיש לך כל מה שצריך להחלטה.</p>
<p>אם התזמון לא מתאים כרגע, אין בעיה בכלל. נשמח לעבוד איתך בכל זמן אחר.</p>
<a href="{{booking_link}}" class="btn">בואו נדבר</a>`,
  });
}

/** 8. Sent when a contract is ready for the client to sign. */
export function contractSent(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Contract is Ready to Sign',
    body: `
<h1>Contract ready to sign.</h1>
<p>Hi {{first_name}},</p>
<p>Your contract for <strong>{{service_interest}}</strong> is ready. Please review and sign at your convenience.</p>
<p>Once signed, we'll send the deposit invoice and lock in your start date.</p>
<p>Questions before signing? Just reply here — happy to walk through anything.</p>
<a href="{{portal_link}}" class="btn">View &amp; Sign Contract</a>`,
  }, {
    subject: 'החוזה שלך מוכן לחתימה',
    body: `
<h1>החוזה מוכן לחתימה.</h1>
<p>שלום {{first_name}},</p>
<p>החוזה שלך עבור <strong>{{service_interest}}</strong> מוכן. אנא עיין/י וחתום/י בנוחות.</p>
<p>לאחר החתימה, נשלח חשבונית מקדמה ונסגור את תאריך ההתחלה.</p>
<p>שאלות לפני החתימה? פשוט השב/י כאן.</p>
<a href="{{portal_link}}" class="btn">צפה וחתום</a>`,
  });
}

/** 9. Confirmation sent immediately after contract is signed. */
export function contractSigned(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: "Contract Signed — Let's Do This! 🎉",
    body: `
<h1>Contract signed. Let's build.</h1>
<p>Hi {{first_name}},</p>
<p>We've received your signed contract for <strong>{{business_name}}</strong>. We're officially on.</p>
<div class="card">
  <div class="lbl">Next step</div>
  <div class="val">Deposit invoice — arriving separately within 24 hours</div>
</div>
<p>Once the deposit clears, we'll schedule your kickoff call and get the ball rolling. Excited to build something great with you.</p>`,
  }, {
    subject: 'החוזה נחתם — בואו נתחיל! 🎉',
    body: `
<h1>החוזה נחתם. בואו נבנה.</h1>
<p>שלום {{first_name}},</p>
<p>קיבלנו את החוזה החתום עבור <strong>{{business_name}}</strong>. אנחנו רשמית מתחילים.</p>
<div class="card">
  <div class="lbl">שלב הבא</div>
  <div class="val">חשבונית מקדמה — תגיע בנפרד תוך 24 שעות</div>
</div>
<p>לאחר קבלת המקדמה, נקבע שיחת קיקאוף ונתחיל לגלגל. מתרגשים לבנות משהו נהדר איתך.</p>`,
  });
}

/** 10. Sent when the deposit invoice is issued. Uses {{email}} and {{phone}} from lead data. */
export function depositInvoiceSent(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Deposit Invoice',
    body: `
<h1>Deposit invoice enclosed.</h1>
<p>Hi {{first_name}},</p>
<p>Your deposit invoice is ready. Paying the deposit locks in your project start date.</p>
<div class="card">
  <div class="lbl">Invoice sent to</div><div class="val">{{email}}</div>
  <div class="lbl">Due</div><div class="val">Upon receipt</div>
</div>
<p>Once payment clears, we'll schedule your kickoff call.</p>`,
  }, {
    subject: 'חשבונית המקדמה שלך',
    body: `
<h1>חשבונית המקדמה מצורפת.</h1>
<p>שלום {{first_name}},</p>
<p>חשבונית המקדמה שלך מוכנה. תשלום המקדמה מאשר את תאריך תחילת הפרויקט.</p>
<div class="card">
  <div class="lbl">החשבונית נשלחה ל</div><div class="val">{{email}}</div>
  <div class="lbl">לתשלום</div><div class="val">עם קבלה</div>
</div>
<p>לאחר קבלת התשלום, נקבע את שיחת הקיקאוף שלך.</p>`,
  });
}

/** 11. Sent when the deposit payment is confirmed received. */
export function depositPaymentReceived(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: "Payment Received — We're Getting Started! ✅",
    body: `
<h1>Payment received. We're on.</h1>
<p>Hi {{first_name}},</p>
<p>We've received your deposit. Your project is officially in queue.</p>
<p>Expect a kickoff call invite within 24 hours. We can't wait to get started.</p>`,
  }, {
    subject: 'התשלום התקבל — מתחילים! ✅',
    body: `
<h1>התשלום התקבל. מתחילים.</h1>
<p>שלום {{first_name}},</p>
<p>קיבלנו את המקדמה שלך. הפרויקט שלך נמצא רשמית בתור.</p>
<p>תקבל/י הזמנה לשיחת קיקאוף תוך 24 שעות. אנחנו לא יכולים לחכות להתחיל.</p>`,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 2 — ONBOARDING (5 templates)
// ═══════════════════════════════════════════════════════════════════════════════

/** 12. First email sent to a new client. Requires {{portal_link}}. */
export function welcomeEmail(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Welcome to Dyna Agency! 🚀',
    body: `
<h1>Welcome to Dyna Agency.</h1>
<p>Hi {{first_name}},</p>
<p>We're genuinely excited to work on <strong>{{business_name}}</strong> with you. Here's what to expect:</p>
<div class="card">
  <div class="lbl">Step 1</div><div class="val">Complete your onboarding questionnaire</div>
  <div class="lbl">Step 2</div><div class="val">Kickoff call — align on vision, goals &amp; timeline</div>
  <div class="lbl">Step 3</div><div class="val">We design, build, and keep you updated every step</div>
  <div class="lbl">Step 4</div><div class="val">Review, approve, and launch</div>
</div>
<p>Your client portal is ready — all project updates, files, and feedback live there.</p>
<a href="{{portal_link}}" class="btn">Open Your Portal</a>`,
  }, {
    subject: 'ברוכים הבאים לסוכנות DYNA! 🚀',
    body: `
<h1>ברוכים הבאים לסוכנות DYNA.</h1>
<p>שלום {{first_name}},</p>
<p>אנחנו ממש נרגשים לעבוד על <strong>{{business_name}}</strong> איתך. הנה מה שצפוי:</p>
<div class="card">
  <div class="lbl">שלב 1</div><div class="val">מלא/י את שאלון הקליטה</div>
  <div class="lbl">שלב 2</div><div class="val">שיחת קיקאוף — יישור קו על חזון, מטרות ולוח זמנים</div>
  <div class="lbl">שלב 3</div><div class="val">אנחנו מעצבים, בונים ומעדכנים אותך בכל שלב</div>
  <div class="lbl">שלב 4</div><div class="val">בדיקה, אישור והשקה</div>
</div>
<p>פורטל הלקוח שלך מוכן — כל עדכוני הפרויקט, הקבצים והמשוב נמצאים שם.</p>
<a href="{{portal_link}}" class="btn">פתח את הפורטל שלך</a>`,
  });
}

/** 13. Nudge sent when the onboarding questionnaire has not been completed. */
export function questionnaireReminder(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: "We're Waiting on Your Questionnaire",
    body: `
<h1>Quick nudge.</h1>
<p>Hi {{first_name}},</p>
<p>We're waiting on your onboarding questionnaire before we can schedule your kickoff call.</p>
<p>It takes about 10–15 minutes and gives us everything we need to hit the ground running.</p>
<a href="{{portal_link}}" class="btn">Complete Questionnaire</a>`,
  }, {
    subject: 'אנחנו מחכים לשאלון שלך',
    body: `
<h1>תזכורת קטנה.</h1>
<p>שלום {{first_name}},</p>
<p>אנחנו מחכים לשאלון הקליטה שלך לפני שנוכל לקבוע את שיחת הקיקאוף.</p>
<p>זה לוקח כ-10–15 דקות ונותן לנו את כל מה שצריך כדי להתחיל מהרוץ.</p>
<a href="{{portal_link}}" class="btn">מלא/י את השאלון</a>`,
  });
}

/** 14. Sent when the kickoff call date/time is confirmed. */
export function kickoffCallScheduled(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Kickoff Call is Scheduled 🎯',
    body: `
<h1>Kickoff call scheduled.</h1>
<p>Hi {{first_name}},</p>
<p>Your kickoff call is set. This is where we align on vision, goals, and timeline before we start building.</p>
<div class="card">
  <div class="lbl">Date</div><div class="val">{{booking_date}}</div>
  <div class="lbl">Time</div><div class="val">{{booking_time}}</div>
</div>
<a href="{{booking_link}}" class="btn">Add to Calendar</a>`,
  }, {
    subject: 'שיחת הקיקאוף נקבעה 🎯',
    body: `
<h1>שיחת הקיקאוף נקבעה.</h1>
<p>שלום {{first_name}},</p>
<p>שיחת הקיקאוף שלך נקבעה. כאן אנחנו מיישרים קו על חזון, מטרות ולוח זמנים לפני שמתחילים לבנות.</p>
<div class="card">
  <div class="lbl">תאריך</div><div class="val">{{booking_date}}</div>
  <div class="lbl">שעה</div><div class="val">{{booking_time}}</div>
</div>
<a href="{{booking_link}}" class="btn">הוסף ליומן</a>`,
  });
}

/** 15. Reminder sent 24 hours before the kickoff call. */
export function kickoffReminder24(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Kickoff Call is Tomorrow!',
    body: `
<h1>Kickoff tomorrow.</h1>
<p>Hi {{first_name}},</p>
<p>Your kickoff call is <strong>tomorrow at {{booking_time}}</strong>. Come ready to share your vision — we'll bring the questions.</p>
<div class="card">
  <div class="lbl">Date</div><div class="val">{{booking_date}}</div>
  <div class="lbl">Time</div><div class="val">{{booking_time}}</div>
</div>
<a href="{{booking_link}}" class="btn">View Details</a>`,
  }, {
    subject: 'שיחת הקיקאוף מחר!',
    body: `
<h1>קיקאוף מחר.</h1>
<p>שלום {{first_name}},</p>
<p>שיחת הקיקאוף שלך היא <strong>מחר בשעה {{booking_time}}</strong>. בואו מוכן/ה לשתף את החזון שלך — אנחנו נביא את השאלות.</p>
<div class="card">
  <div class="lbl">תאריך</div><div class="val">{{booking_date}}</div>
  <div class="lbl">שעה</div><div class="val">{{booking_time}}</div>
</div>
<a href="{{booking_link}}" class="btn">הצג פרטים</a>`,
  });
}

/** 16. Reminder sent 1 hour before the kickoff call. */
export function kickoffReminder1hr(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Kickoff Call Starts in 1 Hour',
    body: `
<h1>Kickoff in 1 hour.</h1>
<p>Hi {{first_name}},</p>
<p>Your kickoff call starts in about an hour at <strong>{{booking_time}}</strong>. Ready to kick things off!</p>
<a href="{{booking_link}}" class="btn">Join Now</a>`,
  }, {
    subject: 'שיחת הקיקאוף מתחילה בעוד שעה',
    body: `
<h1>קיקאוף בעוד שעה.</h1>
<p>שלום {{first_name}},</p>
<p>שיחת הקיקאוף שלך מתחילה בעוד כשעה, בשעה <strong>{{booking_time}}</strong>. מוכן/ה לצאת לדרך!</p>
<a href="{{booking_link}}" class="btn">הצטרף עכשיו</a>`,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 3 — WEB DESIGN (7 templates)
// ═══════════════════════════════════════════════════════════════════════════════

/** 17. Sent when active design/build work begins. */
export function projectKickoffStarted(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: "We've Started Work on Your Website 🛠️",
    body: `
<h1>We've started building.</h1>
<p>Hi {{first_name}},</p>
<p>Your website project is officially underway. We're heads-down on research and design.</p>
<div class="card">
  <div class="lbl">Track progress</div><div class="val"><a href="{{portal_link}}">Your client portal</a></div>
</div>
<p>You'll be able to follow along, leave feedback, and find all project files in your portal. We'll post the first update soon.</p>`,
  }, {
    subject: 'התחלנו לעבוד על האתר שלך 🛠️',
    body: `
<h1>התחלנו לבנות.</h1>
<p>שלום {{first_name}},</p>
<p>פרויקט האתר שלך יצא לדרך רשמית. אנחנו שקועים במחקר ועיצוב.</p>
<div class="card">
  <div class="lbl">עקוב אחרי ההתקדמות</div><div class="val"><a href="{{portal_link}}">הפורטל שלך</a></div>
</div>
<p>תוכל/י לעקוב, להשאיר משוב ולמצוא את כל קבצי הפרויקט בפורטל. נעדכן אותך בקרוב.</p>`,
  });
}

/** 18. Sent when a design mockup is ready for client review. */
export function mockupReady(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Mockup is Ready for Review 👀',
    body: `
<h1>Design mockup ready.</h1>
<p>Hi {{first_name}},</p>
<p>Your design mockup is ready for review. Take a look and share your thoughts — we want to make sure you love it before we start building.</p>
<a href="{{portal_link}}" class="btn">View Mockup</a>
<p>Please share feedback within 3 business days to keep the project on schedule. Leave comments in the portal or reply to this email.</p>`,
  }, {
    subject: 'המוקאפ שלך מוכן לבדיקה 👀',
    body: `
<h1>מוקאפ העיצוב מוכן.</h1>
<p>שלום {{first_name}},</p>
<p>מוקאפ העיצוב שלך מוכן לבדיקה. עיין/י ושתף/י את מחשבותיך — אנחנו רוצים לוודא שאתה/את אוהב/ת אותו לפני שנתחיל לבנות.</p>
<a href="{{portal_link}}" class="btn">צפה במוקאפ</a>
<p>אנא שתף/י משוב תוך 3 ימי עסקים כדי לשמור על לוח הזמנים. השאר/י הערות בפורטל או השב/י לאימייל זה.</p>`,
  });
}

/** 19. Sent when a revision round is complete. Uses {{revision_number}}. */
export function revisionReady(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Revision Round {{revision_number}} Ready',
    body: `
<h1>Revision {{revision_number}} is in.</h1>
<p>Hi {{first_name}},</p>
<p>We've applied your feedback. Revision round <strong>{{revision_number}}</strong> is ready for your review.</p>
<a href="{{portal_link}}" class="btn">Review Changes</a>
<p>Let us know what you think. Feedback in the portal keeps everything organised and on track.</p>`,
  }, {
    subject: 'סבב תיקונים {{revision_number}} מוכן',
    body: `
<h1>תיקון {{revision_number}} מוכן.</h1>
<p>שלום {{first_name}},</p>
<p>יישמנו את המשוב שלך. סבב תיקונים <strong>{{revision_number}}</strong> מוכן לבדיקתך.</p>
<a href="{{portal_link}}" class="btn">בדוק שינויים</a>
<p>ספר/י לנו מה אתה/את חושב/ת. משוב בפורטל שומר על הכל מאורגן ובלוח הזמנים.</p>`,
  });
}

/** 20. Final request for client approval before going live. */
export function finalApprovalRequest(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Final Approval Needed 🏁',
    body: `
<h1>Ready for final approval.</h1>
<p>Hi {{first_name}},</p>
<p>Your website is complete and ready for your final sign-off before we go live.</p>
<a href="{{portal_link}}" class="btn">Review Final Version</a>
<p>Once you approve, we'll schedule the launch. If anything needs a last tweak, just let us know.</p>`,
  }, {
    subject: 'נדרש אישור סופי 🏁',
    body: `
<h1>מוכן לאישור סופי.</h1>
<p>שלום {{first_name}},</p>
<p>האתר שלך הושלם ומחכה לאישורך הסופי לפני שנעלה אותו לאוויר.</p>
<a href="{{portal_link}}" class="btn">בדוק גרסה סופית</a>
<p>לאחר אישורך, נקבע את תאריך ההשקה. אם משהו צריך תיקון אחרון, פשוט אמור/י לנו.</p>`,
  });
}

/** 21. Sent the moment the website goes live. */
export function launchDay(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: '🚀 Your Website is LIVE!',
    body: `
<h1>🚀 You're live.</h1>
<p>Hi {{first_name}},</p>
<p>Congratulations — your website is officially live. This is a big moment and we're proud to have built it with you.</p>
<div class="card">
  <div class="lbl">Your site</div>
  <div class="val"><a href="{{portal_link}}">Visit your website</a></div>
</div>
<p>Share it with the world. We'll check in with you in 30 days to see how it's performing.</p>`,
  }, {
    subject: '🚀 האתר שלך עלה לאוויר!',
    body: `
<h1>🚀 אתה/את בשידור חי.</h1>
<p>שלום {{first_name}},</p>
<p>מזל טוב — האתר שלך עלה לאוויר רשמית. זה רגע גדול ואנחנו גאים שבנינו אותו איתך.</p>
<div class="card">
  <div class="lbl">האתר שלך</div>
  <div class="val"><a href="{{portal_link}}">בקר באתר שלך</a></div>
</div>
<p>שתף/י עם העולם. נחזור אליך בעוד 30 יום לראות איך הוא מתפקד.</p>`,
  });
}

/** 22. Check-in sent 30 days after launch. */
export function thirtyDayCheckIn(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: "30 Days Live — How's Everything?",
    body: `
<h1>30 days in.</h1>
<p>Hi {{first_name}},</p>
<p>It's been 30 days since your site went live. How's it going?</p>
<p>We'd love to hear your feedback — and if there are any final tweaks before we close the project, now's the time.</p>
<a href="{{booking_link}}" class="btn">Book a Check-In Call</a>`,
  }, {
    subject: '30 יום באוויר — הכל בסדר?',
    body: `
<h1>30 יום לאחר ההשקה.</h1>
<p>שלום {{first_name}},</p>
<p>עברו 30 יום מאז שהאתר שלך עלה לאוויר. איך הולך?</p>
<p>נשמח לשמוע את המשוב שלך — ואם יש תיקונים אחרונים לפני שנסגור את הפרויקט, עכשיו הזמן.</p>
<a href="{{booking_link}}" class="btn">קבע שיחת מעקב</a>`,
  });
}

/** 23. Final email closing out a completed project. */
export function finalHandoff(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Website Handoff Complete',
    body: `
<h1>Project complete.</h1>
<p>Hi {{first_name}},</p>
<p>The <strong>{{business_name}}</strong> project is fully handed off. All credentials, files, and documentation are in your client portal.</p>
<div class="card">
  <div class="lbl">Find everything in</div>
  <div class="val">Client Portal → Files &amp; Documents</div>
</div>
<p>It was a genuine pleasure building with you. We hope to work together again.</p>
<a href="{{portal_link}}" class="btn">Open Your Portal</a>`,
  }, {
    subject: 'מסירת האתר הושלמה',
    body: `
<h1>הפרויקט הושלם.</h1>
<p>שלום {{first_name}},</p>
<p>פרויקט <strong>{{business_name}}</strong> נמסר במלואו. כל הגישות, הקבצים והתיעוד נמצאים בפורטל הלקוח שלך.</p>
<div class="card">
  <div class="lbl">מצא הכל ב</div>
  <div class="val">פורטל לקוח ← קבצים ומסמכים</div>
</div>
<p>היה עונג אמיתי לבנות איתך. נשמח לשתף פעולה שוב.</p>
<a href="{{portal_link}}" class="btn">פתח את הפורטל שלך</a>`,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 4 — SEO/GEO RETAINER (7 templates)
// ═══════════════════════════════════════════════════════════════════════════════

/** 24. Monthly SEO/GEO performance report notification. Uses {{month}}, {{dashboard_link}}. */
export function monthlyReportReady(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your {{month}} SEO/GEO Report is Ready',
    body: `
<h1>{{month}} report ready.</h1>
<p>Hi {{first_name}},</p>
<p>Your <strong>{{month}}</strong> SEO/GEO report is ready in your portal. Open it to see rankings, traffic changes, and what we're focused on next month.</p>
<div class="card">
  <div class="lbl">Full report</div><div class="val"><a href="{{portal_link}}">View in Portal</a></div>
  <div class="lbl">Live dashboard</div><div class="val"><a href="{{dashboard_link}}">Open Dashboard</a></div>
</div>
<a href="{{portal_link}}" class="btn">View Report</a>
<p>Questions? <a href="{{booking_link}}">Book a 15-minute call</a> and we'll walk through it together.</p>`,
  }, {
    subject: 'דוח ה-SEO/GEO של {{month}} מוכן',
    body: `
<h1>דוח {{month}} מוכן.</h1>
<p>שלום {{first_name}},</p>
<p>דוח ה-SEO/GEO של <strong>{{month}}</strong> שלך מוכן בפורטל. פתח/י אותו לראות דירוגים, שינויי תנועה ומה אנחנו עובדים עליו בחודש הבא.</p>
<div class="card">
  <div class="lbl">הדוח המלא</div><div class="val"><a href="{{portal_link}}">צפה בפורטל</a></div>
  <div class="lbl">לוח מחוונים חי</div><div class="val"><a href="{{dashboard_link}}">פתח לוח מחוונים</a></div>
</div>
<a href="{{portal_link}}" class="btn">צפה בדוח</a>
<p>שאלות? <a href="{{booking_link}}">קבע שיחה של 15 דקות</a> ונעבור עליו יחד.</p>`,
  });
}

/** 25. Monthly retainer invoice notification. Uses {{month}}, {{package}}, {{email}}. */
export function monthlyInvoiceSent(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Your Invoice for {{month}}',
    body: `
<h1>{{month}} invoice.</h1>
<p>Hi {{first_name}},</p>
<p>Your monthly retainer invoice for <strong>{{month}}</strong> has been sent to <strong>{{email}}</strong>.</p>
<div class="card">
  <div class="lbl">Package</div><div class="val">{{package}}</div>
</div>
<p>If you have any questions about this invoice, just reply here.</p>`,
  }, {
    subject: 'החשבונית שלך עבור {{month}}',
    body: `
<h1>חשבונית {{month}}.</h1>
<p>שלום {{first_name}},</p>
<p>חשבונית הרטיינר החודשית שלך עבור <strong>{{month}}</strong> נשלחה ל-<strong>{{email}}</strong>.</p>
<div class="card">
  <div class="lbl">חבילה</div><div class="val">{{package}}</div>
</div>
<p>שאלות על החשבונית? פשוט השב/י כאן.</p>`,
  });
}

/** 26. Payment confirmation for a retainer month. Uses {{month}}. */
export function paymentReceived(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Payment Received ✅',
    body: `
<h1>Payment received.</h1>
<p>Hi {{first_name}},</p>
<p>We've received your payment for <strong>{{month}}</strong>. Thank you — we'll keep the work running smoothly.</p>`,
  }, {
    subject: 'התשלום התקבל ✅',
    body: `
<h1>התשלום התקבל.</h1>
<p>שלום {{first_name}},</p>
<p>קיבלנו את תשלומך עבור <strong>{{month}}</strong>. תודה — נמשיך לעבוד בצורה חלקה.</p>`,
  });
}

/** 27. Overdue invoice alert. Requires {{month}}, {{portal_link}}. */
export function paymentOverdue(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Invoice Overdue — Action Required',
    body: `
<h1>Invoice overdue.</h1>
<p>Hi {{first_name}},</p>
<p>Your invoice for <strong>{{month}}</strong> is past due. Please pay at your earliest convenience to avoid any interruption to your SEO/GEO services.</p>
<a href="{{portal_link}}" class="btn">Pay Now</a>
<p>If you need to arrange a different payment date, just reply to this email.</p>`,
  }, {
    subject: 'חשבונית באיחור — נדרשת פעולה',
    body: `
<h1>חשבונית באיחור.</h1>
<p>שלום {{first_name}},</p>
<p>החשבונית שלך עבור <strong>{{month}}</strong> פגת מועד. אנא שלם/י בהקדם האפשרי כדי להימנע מהפרעה לשירותי ה-SEO/GEO שלך.</p>
<a href="{{portal_link}}" class="btn">שלם עכשיו</a>
<p>אם צריך לסדר תאריך תשלום אחר, פשוט השב/י לאימייל זה.</p>`,
  });
}

/** 28. Milestone email sent at the 6-month mark of a retainer. */
export function sixMonthMilestone(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: "6 Months In — Look How Far We've Come 🎉",
    body: `
<h1>6 months in.</h1>
<p>Hi {{first_name}},</p>
<p>We've been working together for 6 months and we want to take a moment to appreciate that. Your online presence has grown meaningfully and we're proud of what we've built together.</p>
<p>We'd love to do a milestone review — walk through the numbers, set new goals, and plan the next 6 months.</p>
<a href="{{booking_link}}" class="btn">Book Your Milestone Review</a>
<p>Thank you for trusting us with your growth.</p>`,
  }, {
    subject: '6 חודשים — הגענו רחוק 🎉',
    body: `
<h1>6 חודשים ביחד.</h1>
<p>שלום {{first_name}},</p>
<p>עברו 6 חודשים שאנחנו עובדים יחד ורצינו לעצור רגע לסמן את זה. הנוכחות הדיגיטלית שלך גדלה משמעותית ואנחנו גאים במה שבנינו יחד.</p>
<p>נשמח לעשות סקירת אבן דרך — לעבור על המספרים, להציב מטרות חדשות ולתכנן את 6 החודשים הבאים.</p>
<a href="{{booking_link}}" class="btn">קבע סקירת אבן דרך</a>
<p>תודה שנתת לנו לטפל בצמיחה שלך.</p>`,
  });
}

/** 29. Referral program invite for active clients. */
export function referralProgram(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Get 20% Off — Refer a Business',
    body: `
<h1>Refer a business. Save 20%.</h1>
<p>Hi {{first_name}},</p>
<p>You've been one of our best clients at <strong>{{business_name}}</strong> and we grow almost entirely through word of mouth. If you know another business owner who could use what we do — we'd love the introduction.</p>
<div class="card">
  <div class="lbl">Your reward</div>
  <div class="val"><strong>20% off one month's retainer</strong> for every client you refer who signs</div>
</div>
<p>Just forward this email or reply with their details — we'll handle the rest.</p>`,
  }, {
    subject: 'קבלו 20% הנחה — הפנו עסק',
    body: `
<h1>הפנה עסק. חסוך 20%.</h1>
<p>שלום {{first_name}},</p>
<p>היית אחד הלקוחות הטובים שלנו ב-<strong>{{business_name}}</strong> ואנחנו גדלים כמעט לגמרי מפה לאוזן. אם אתה/את מכיר/ה בעלי עסקים שיכולים להיעזר בשירותים שלנו — נשמח להיפגש איתם.</p>
<div class="card">
  <div class="lbl">הפרס שלך</div>
  <div class="val"><strong>20% הנחה על חודש רטיינר אחד</strong> על כל לקוח שמפנה וחותם</div>
</div>
<p>פשוט העבר/י את האימייל הזה או השב/י עם הפרטים שלהם — אנחנו נטפל בשאר.</p>`,
  });
}

/**
 * 30. Bi-weekly progress update.
 * Uses {{month}}, {{week}}, {{completed}}, {{next_up}}, {{need_from_client}}.
 */
export function biWeeklyCheckIn(data: Record<string, string>, lang: Lang): TemplateResult {
  return build(data, lang, {
    subject: 'Bi-Weekly Check-In — {{month}} Week {{week}}',
    body: `
<h1>Bi-weekly check-in.</h1>
<p>Hi {{first_name}},</p>
<p>Quick update from the Dyna team — here's where things stand for <strong>{{month}}, Week {{week}}</strong>:</p>
<div class="card">
  <div class="lbl">Completed this period</div><div class="val">{{completed}}</div>
  <div class="lbl">Next up</div><div class="val">{{next_up}}</div>
  <div class="lbl">We need from you</div><div class="val">{{need_from_client}}</div>
</div>
<p>Any questions or feedback? Just reply here.</p>`,
  }, {
    subject: 'עדכון דו-שבועי — {{month}} שבוע {{week}}',
    body: `
<h1>עדכון דו-שבועי.</h1>
<p>שלום {{first_name}},</p>
<p>עדכון קצר מצוות Dyna — הנה מה שקורה ב-<strong>{{month}}, שבוע {{week}}</strong>:</p>
<div class="card">
  <div class="lbl">הושלם בתקופה זו</div><div class="val">{{completed}}</div>
  <div class="lbl">הבא בתור</div><div class="val">{{next_up}}</div>
  <div class="lbl">אנחנו צריכים ממך</div><div class="val">{{need_from_client}}</div>
</div>
<p>שאלות או משוב? פשוט השב/י כאן.</p>`,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Registry — keyed by template name for programmatic access
// ═══════════════════════════════════════════════════════════════════════════════

export const EMAIL_TEMPLATES = {
  // Lead & Sales
  inquiryResponse,
  discoveryCallConfirmation,
  discoveryCallReminder24,
  discoveryCallReminder1hr,
  proposalSent,
  proposalAccepted,
  proposalDeclined,
  proposalFollowUp3,
  proposalFollowUp7,
  contractSent,
  contractSigned,
  depositInvoiceSent,
  depositPaymentReceived,
  // Onboarding
  welcomeEmail,
  questionnaireReminder,
  kickoffCallScheduled,
  kickoffReminder24,
  kickoffReminder1hr,
  // Web Design
  projectKickoffStarted,
  mockupReady,
  revisionReady,
  finalApprovalRequest,
  launchDay,
  thirtyDayCheckIn,
  finalHandoff,
  // SEO/GEO Retainer
  monthlyReportReady,
  monthlyInvoiceSent,
  paymentReceived,
  paymentOverdue,
  sixMonthMilestone,
  referralProgram,
  biWeeklyCheckIn,
} as const;

export type TemplateName = keyof typeof EMAIL_TEMPLATES;
